/** @format */

/* eslint react-hooks/rules-of-hooks: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, useRef } from 'react';
import styled from 'styled-components';

import ArrowSmallIconLeft from '@toucan/client/global/icons/legacy/arrow-small-left.svg';
import ArrowSmallIconRight from '@toucan/client/global/icons/legacy/arrow-small-right.svg';

import {
    IconButton,
    IconButtonFill, // eslint-disable-next-line boundaries/element-types -- This should be using the icon from feather
} from '@toucan/client/shared/components/icon-button';
// eslint-disable-next-line boundaries/element-types -- This should be using the feather version of the hooks
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';

import {
    PaginationItemProps,
    PaginationItemPropsWithPageNum,
    PaginationVariant,
    clampToPaginationRange,
    getElements,
    itemsInRange,
} from './pagination.helpers';
import { styles } from './pagination.styles';

export {
    PaginationVariant,
    type PaginationItemProps,
} from './pagination.helpers';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface PaginationProps {
    /**
     * The current page. indexed at 0
     */
    currentItem: number;

    /**
     * An array of items to be displayed in the pagination component
     */
    items: PaginationItemProps[];

    /**
     * The pagination variant, one of:
     * 1. NUMBERED
     * 2. THUMBNAIL
     * 3. DOT
     *
     * @default NUMBERED
     */
    variant?: PaginationVariant;

    /**
     * The number of items to show at a time in the pagination.
     * All items will be shown when this prop isn't passed.
     */
    itemLimit?: number;

    /**
     * Whether or not to always show first and last buttons
     * before and after pagination items.
     *
     * @default true
     */
    showFirstLastButtons?: boolean;

    /**
     * Whether or not to show previous and next buttons
     * before and after pagination items.
     *
     * @default false
     */
    showPrevNextButtons?: boolean;

    /**
     * Allows a custom label to be applied to a pagination item
     *
     * @param index - The index of the item we are applying the label to.
     */
    itemLabel?: (index: number) => string;

    /**
     * Handler for when new pagination page is selected
     *
     * @param index - The index of the new selected page
     */
    onPage?: (index: number) => void;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * A label for the previous button
     */
    previousLabel?: string;

    /**
     * A label for a next button
     */
    nextLabel?: string;

    /**
     * If the pagination controls should loop.
     * E.g pressing right arrow when last item is selected will go back
     * to beginning
     *
     * @default true
     */
    loop?: boolean;

    /**
     * Custom className
     */
    className?: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface PaginationPropsWithButton extends PaginationProps {
    showPrevNextButtons: true;
    previousLabel: string;
    nextLabel: string;
}

const StyledPagination = styled('nav').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const Pagination: FunctionComponent<
    PaginationProps | PaginationPropsWithButton
> = ({
    items,
    itemLimit,
    currentItem = 0,
    variant = PaginationVariant.NUMBERED,
    showFirstLastButtons = true,
    showPrevNextButtons = false,
    previousLabel = '',
    nextLabel = '',
    itemLabel,
    loop = true,
    onPage,
    ...otherProps
}: PaginationProps | PaginationPropsWithButton) => {
    const { t } = useTranslation();
    const paginationRef = useRef<HTMLOListElement>(null);

    const numItems = items.length;
    // Ensure currentItem property is within bounds
    const clampedCurrentItem = clampToPaginationRange(
        currentItem,
        items.length
    );

    // Assign each item a pageNum so we can emit an event with the items original position
    const itemsWithPageNum: PaginationItemPropsWithPageNum[] = items.map(
        (item: PaginationItemProps, i: number) => {
            return {
                ...item,
                pageNum: i,
            };
        }
    );

    const filteredItems = itemsInRange(
        itemsWithPageNum,
        currentItem,
        showFirstLastButtons,
        itemLimit
    );

    const paginationElements = getElements(
        variant,
        filteredItems,
        numItems,
        currentItem,
        t,
        showFirstLastButtons,
        itemLabel,
        onPage
    );

    /**
     * Handle 'left' and 'down' key presses decrements position in the pagination.
     * When key presses are combined with the 'shift' modifier the pagination
     * will move to the 0 position.
     */
    useKeyPress(
        paginationRef.current,
        ['ArrowLeft', 'Left'],
        (event: KeyboardEvent) => {
            event.preventDefault();
            if (event.getModifierState('Shift')) {
                onPage && onPage(0);
            } else {
                if (!loop && clampedCurrentItem === 0) {
                    return;
                }

                onPage &&
                    onPage(
                        (clampedCurrentItem - 1 + items.length) % items.length
                    );
            }
        }
    );

    /**
     * Handle 'right' and 'up' key presses increments position in the pagination.
     * When key presses are combined with the 'shift' modifier the pagination
     * will move to the last pagination page.
     */
    useKeyPress(
        paginationRef.current,
        ['ArrowRight', 'Right'],
        (event: KeyboardEvent) => {
            event.preventDefault();
            if (event.getModifierState('Shift')) {
                onPage && onPage(items.length - 1);
            } else {
                if (!loop && clampedCurrentItem + 1 === items.length) {
                    return;
                }

                onPage && onPage((clampedCurrentItem + 1) % items.length);
            }
        }
    );

    if (!items.length) {
        return null;
    }

    return (
        <StyledPagination
            items={items}
            itemLimit={itemLimit}
            currentItem={clampedCurrentItem}
            variant={variant}
            showFirstLastButtons={showFirstLastButtons}
            showPrevNextButtons={showPrevNextButtons}
            {...otherProps}
        >
            {showPrevNextButtons && (
                <IconButton
                    label={previousLabel}
                    border={true}
                    href={itemsWithPageNum[clampedCurrentItem - 1]?.href}
                    fill={IconButtonFill.HOLLOW}
                    onClick={(event) => {
                        event.preventDefault();
                        onPage &&
                            onPage(
                                clampToPaginationRange(
                                    currentItem - 1,
                                    items.length
                                )
                            );
                    }}
                    disabled={!showPrevNextButtons || currentItem === 0}
                >
                    <ArrowSmallIconLeft />
                </IconButton>
            )}
            <ol ref={paginationRef} tabIndex={0}>
                {paginationElements}
            </ol>
            {showPrevNextButtons && (
                <IconButton
                    label={nextLabel}
                    border={true}
                    href={itemsWithPageNum[currentItem + 1]?.href}
                    fill={IconButtonFill.HOLLOW}
                    onClick={(event) => {
                        event.preventDefault();
                        onPage &&
                            onPage(
                                clampToPaginationRange(
                                    clampedCurrentItem + 1,
                                    items.length
                                )
                            );
                    }}
                    disabled={
                        !showPrevNextButtons ||
                        clampedCurrentItem === items.length - 1
                    }
                >
                    <ArrowSmallIconRight />
                </IconButton>
            )}
        </StyledPagination>
    );
};
